import { useEffect } from 'react'
import Loading from '@/components/Loading'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { ErrorEnum } from '@/utils/announcements'
import type { PropsWithChildren } from 'react'
import { setLoggerUser } from '@/logger/logger'
import { useUser } from '@clerk/shared/react'
import {useAnnouncementsContext} from "@/contexts/AnnouncementsContext";

export interface ClerkJWTPayload {
  authToken: string
  [key: string]: unknown
}

export const AuthIntermediator = ({ children }: PropsWithChildren) => {
  const { isSignedIn } = useUser()
  const { data: user, isError } = useCurrentUser()

  const { addAnnouncement, removeAnnouncement } = useAnnouncementsContext()

  // A failed *background* refetch leaves `isError` true while the cached token
  // is still perfectly usable — TanStack keeps `data` on error by design. Only
  // a missing token actually blocks us, so gate on that rather than on `isError`
  // alone, or a transient blip after `staleTime` would tear down a live session.
  const canProceed = Boolean(isSignedIn && user?.token)

  useEffect(() => {
    setLoggerUser(user)
  }, [user])

  useEffect(() => {
    if(isError && !canProceed && addAnnouncement) {
      addAnnouncement(ErrorEnum.PROFILE_LOAD_ERROR)
      return () => removeAnnouncement(ErrorEnum.PROFILE_LOAD_ERROR)
    }
  }, [addAnnouncement, isError, canProceed, removeAnnouncement])

  if (canProceed) {
    return <>{children}</>
  }

  // The announcement renders from AppShell as a sibling of the Outlet, so this
  // subtree has nothing left to draw underneath it.
  if (isError) {
    return null
  }

  return <Loading message="Loading..." />
}
